/**
 * How citations are found in an open editor.
 *
 * The hover layer needs three things: every citation on the page, the painted
 * line fragments each one occupies (for hit-testing and anchoring the card),
 * and which one the caret sits in. Where those come from depends on the
 * engine:
 *
 * - eigenpal 1.8.3 keeps content controls in the document model as inline
 *   `sdt` nodes and paints plain text runs with `data-pm-start`/`data-pm-end`.
 *   Citations are located in the model and mapped onto runs by position.
 * - the legacy 2.10 engine paints a `.docx-content-control-chrome[data-tag]`
 *   element per control with one boundary box per line fragment and flags the
 *   one holding the caret `data-active`.
 */
import type { DocxSurfaceHandle } from "../draftEditor/surface";

import {
  CITATION_CHROME_SELECTOR,
  CITATION_FRAGMENT_SELECTOR,
  citationKey,
  parseCitationTag,
  type CitationRef
} from "./model";

export type LocatedCitation = CitationRef & {
  /** Identity of the citation (node + page), stable across repaints. */
  key: string;
  /** Distinguishes two controls citing the same thing; stable across repaints. */
  id: string;
};

export type CitationLocator = {
  /** Every citation in document order. */
  list: () => LocatedCitation[];
  /** Painted line fragments of a citation, in paint order. */
  fragments: (citation: LocatedCitation) => Element[];
  /** The citation the caret sits in, if any. */
  atCaret: () => LocatedCitation | null;
  /**
   * Stamp `data-citation` on the painted fragments so CSS can style them at
   * rest. Runs after every repaint; a no-op where the editor's own chrome
   * already carries the tag.
   */
  decorate: () => void;
  /** Attributes whose changes should re-evaluate the caret. */
  attributeFilter: string[];
};

/** Painted spans that overlap a document range, in paint order. */
const PAINTED_RUN_SELECTOR = "span[data-pm-start][data-pm-end]";

const overlaps = (element: Element, from: number, to: number) => {
  const start = Number((element as HTMLElement).dataset.pmStart);
  const end = Number((element as HTMLElement).dataset.pmEnd);
  return (
    Number.isFinite(start) && Number.isFinite(end) && start < to && end > from
  );
};

export const CITATION_DATA_ATTR = "data-citation";

/** Citations from the document model, mapped onto painted runs (eigenpal). */
export const modelCitationLocator = (
  host: HTMLElement,
  surface: DocxSurfaceHandle
): CitationLocator => {
  type Located = LocatedCitation & { from: number; to: number };
  const list = (): Located[] => {
    const out: Located[] = [];
    for (const control of surface.getInlineControls()) {
      const ref = parseCitationTag(control.tag);
      if (ref) {
        out.push({
          ...ref,
          key: citationKey(ref),
          id: `${control.from}:${control.to}`,
          from: control.from,
          to: control.to
        });
      }
    }
    return out;
  };
  const rangeOf = (citation: LocatedCitation): [number, number] => {
    const [from, to] = citation.id.split(":").map(Number);
    return [from, to];
  };
  const fragments = (citation: LocatedCitation) => {
    const [from, to] = rangeOf(citation);
    return Array.from(host.querySelectorAll(PAINTED_RUN_SELECTOR)).filter(
      (element) => overlaps(element, from, to)
    );
  };
  return {
    list,
    fragments,
    atCaret: () => {
      const caret = surface.getCaretPosition();
      if (caret === null) return null;
      return (
        list().find(
          (citation) => caret >= citation.from && caret <= citation.to
        ) ?? null
      );
    },
    decorate: () => {
      const citations = list();
      for (const run of Array.from(
        host.querySelectorAll(PAINTED_RUN_SELECTOR)
      )) {
        const owner = citations.find((citation) =>
          overlaps(run, citation.from, citation.to)
        );
        if (owner) run.setAttribute(CITATION_DATA_ATTR, owner.key);
        else if (run.hasAttribute(CITATION_DATA_ATTR))
          run.removeAttribute(CITATION_DATA_ATTR);
      }
    },
    attributeFilter: ["data-pm-start", "data-pm-end"]
  };
};

/** Citations from the editor's painted chrome (legacy 2.10). */
export const chromeCitationLocator = (host: HTMLElement): CitationLocator => {
  const chromes = () =>
    Array.from(host.querySelectorAll(CITATION_CHROME_SELECTOR));
  const locate = (chrome: Element, index: number): LocatedCitation | null => {
    const ref = parseCitationTag(chrome.getAttribute("data-tag"));
    return ref ? { ...ref, key: citationKey(ref), id: String(index) } : null;
  };
  const chromeOf = (citation: LocatedCitation) =>
    chromes()[Number(citation.id)] ?? null;
  return {
    list: () =>
      chromes()
        .map(locate)
        .filter((citation): citation is LocatedCitation => citation !== null),
    fragments: (citation) => {
      const chrome = chromeOf(citation);
      return chrome
        ? Array.from(chrome.querySelectorAll(CITATION_FRAGMENT_SELECTOR))
        : [];
    },
    atCaret: () => {
      const all = chromes();
      const index = all.findIndex((chrome) =>
        chrome.hasAttribute("data-active")
      );
      return index === -1 ? null : locate(all[index], index);
    },
    decorate: () => undefined,
    attributeFilter: ["data-active", "data-tag"]
  };
};

/** The locator that fits the mounted surface. */
export const citationLocatorFor = (
  host: HTMLElement,
  surface: DocxSurfaceHandle | null
): CitationLocator =>
  surface && surface.engine !== "legacy"
    ? modelCitationLocator(host, surface)
    : chromeCitationLocator(host);
